import { type FC, type ReactNode, useEffect, useMemo } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useLocale } from "@jti/app-context/useLocale";
import { Loader } from "@jti/core-components/components/Loader";
import { GoogleAPIProvider } from "@jti/fe-clients";
import { useGetOrderDeliveryLimitsQuery, useGetStoreConfigQuery } from "@jti/store/slices/magentoApiSlice";
import { AddressFormProvider, useFormContext } from "@jti/ui";

import { useCountry } from "@checkout-v2/shared/hooks/useCountry";

import { prepareMagentoPostCodeConfig } from "../AddressHomeDeliveryField/utils/prepareMagentoPostCodeConfig";

import styles from "./AddressFormFields.module.scss";

type AddressFormFieldsProps = {
  fieldNameBase: string;
  children?: ReactNode;
  beforeSlot?: ReactNode;
};

export const AddressFormFields: FC<AddressFormFieldsProps> = ({ fieldNameBase, children, beforeSlot }) => {
  const config = useConfig();
  const locale = useLocale();
  const formContext = useFormContext();

  const orderDeliveryLimitsQueryState = useGetOrderDeliveryLimitsQuery();
  const storeConfigQueryState = useGetStoreConfigQuery();
  const countryQueryState = useCountry();

  const { availableRegions } = countryQueryState.data ?? {};

  const postCodeConfig = useMemo(() => {
    if (!storeConfigQueryState.data) {
      return undefined;
    }

    return prepareMagentoPostCodeConfig(storeConfigQueryState.data, config);
  }, [config, storeConfigQueryState.data]);

  const isLoading =
    orderDeliveryLimitsQueryState.isLoading || storeConfigQueryState.isLoading || countryQueryState.isLoading;

  useEffect(function setCountryValuesFromConfig() {
    formContext.setValue(`${fieldNameBase}.countryCode`, config.ISO_COUNTRY_CODE);
    formContext.setValue(`${fieldNameBase}.countryName`, config.COUNTRY_NAME);
    // TODO - fix deps arrays values after exhaustive-deps as error - DESP-111178
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GoogleAPIProvider
      apiKey={config.GOOGLE_MAPS_API}
      options={{
        version: "weekly",
        libraries: ["geometry", "places"],
        language: locale,
        region: config.ISO_COUNTRY_CODE,
      }}
    >
      {isLoading && <Loader />}

      {!isLoading && (
        <>
          <div className={styles.beforeSlotAndAddressForm}>
            {beforeSlot}
            <AddressFormProvider
              key={fieldNameBase}
              orderDeliveryLimits={orderDeliveryLimitsQueryState.data}
              locationServiceAutocompleteProvider={config.locationServiceAutocompleteProvider}
              formType={config.addressFormVariant}
              fieldName={(fieldName: string) => `${fieldNameBase}.${fieldName}`}
              postCodeConfig={postCodeConfig}
              regions={availableRegions}
              isSingleFieldAddressLookup={config.isSingleFieldAddressLookup}
              isStreetFieldCombined={config.isStreetFieldCombined}
              ksaProvinces={config.ksaProvinces}
            />
          </div>

          {children}
        </>
      )}
    </GoogleAPIProvider>
  );
};
